import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { partnerDashboardSliceAction } from "../../../redux/slices/partner-dashboard";
import {
  mapActiveContractKpi,
  mapContractStatusToCardData,
  mapProjectStatsToDashboard,
  mapRecentOrdersToCardData,
  mapSummaryToKpiCards,
  mapSummaryToPartnerProgram,
  mapSupportStatusFromSummary,
  EMPTY_PROJECT_STATUS,
  PLACEHOLDER_ACTIVE_CONTRACT_KPI,
  PLACEHOLDER_CONTRACT_STATUS,
  PLACEHOLDER_KPI_CARDS,
  PLACEHOLDER_PARTNER_PROGRAM,
} from "../../../redux/slices/partner-dashboard/partnerDashboardMapper";
import {
  getPartnerDashboardSagaAction,
  sendContractReminderSagaAction,
  updatePartnerSupportLevelSagaAction,
} from "../../../redux/saga/partner-dashboard/saga-actions";
import { STATIC_PARTNER_DASHBOARD_DATA } from "../data/staticDashboardData";
import { PartnerDashboardData } from "../types";

export type PartnerDashboardViewData = PartnerDashboardData & {
  isLoading: boolean;
  isSupportStatusSaving: boolean;
  supportStatusError: string | null;
  saveSupportStatus: (payload: {
    support_level: string;
    support_status: number;
  }) => void;
  clearSupportStatusError: () => void;
  sendContractReminder: () => void;
  isContractReminderSending: boolean;
};

export function usePartnerDashboardData(
  partnerId?: number
): PartnerDashboardViewData {
  const dispatch = useAppDispatch();
  const { data, partnerId: loadedPartnerId, status } = useAppSelector(
    (state) => state.partner_dashboard
  );

  const hasValidPartnerId = Boolean(partnerId) && !Number.isNaN(partnerId);
  const isCurrentPartner = loadedPartnerId === partnerId;
  const isLoading =
    hasValidPartnerId && (status.loading || loadedPartnerId !== partnerId);

  useEffect(() => {
    if (!partnerId || Number.isNaN(partnerId)) {
      return;
    }

    dispatch(getPartnerDashboardSagaAction(partnerId));

    return () => {
      dispatch(partnerDashboardSliceAction.clearPartnerDashboard());
    };
  }, [dispatch, partnerId]);

  const clearSupportStatusError = useCallback(() => {
    dispatch(partnerDashboardSliceAction.setSupportStatusError(null));
  }, [dispatch]);

  const saveSupportStatus = useCallback(
    (payload: { support_level: string; support_status: number }) => {
      if (!partnerId || Number.isNaN(partnerId)) {
        return;
      }

      dispatch(
        updatePartnerSupportLevelSagaAction({
          partnerId,
          ...payload,
        })
      );
    },
    [dispatch, partnerId]
  );

  const sendContractReminder = useCallback(() => {
    if (!partnerId || Number.isNaN(partnerId)) {
      return;
    }

    dispatch(sendContractReminderSagaAction(partnerId));
  }, [dispatch, partnerId]);

  const activeContractKpi = useMemo(() => {
    const summary = isCurrentPartner ? data?.summary ?? null : null;
    const apiContractStatus = isCurrentPartner
      ? data?.contractStatus ?? null
      : null;

    if (summary && apiContractStatus) {
      return mapActiveContractKpi(apiContractStatus);
    }

    return PLACEHOLDER_ACTIVE_CONTRACT_KPI;
  }, [data?.summary, data?.contractStatus, isCurrentPartner]);

  const kpiCards = useMemo(() => {
    const summary = isCurrentPartner ? data?.summary ?? null : null;

  const completedIntegrations =
    isCurrentPartner ? data?.projectStats?.completed_integrations ?? 0 : 0;

  const [partnerSince, ppmTier, tierScore] = summary
    ? mapSummaryToKpiCards(summary, completedIntegrations)
    : PLACEHOLDER_KPI_CARDS;

    return [partnerSince, activeContractKpi, ppmTier, tierScore];
  }, [data?.summary, data?.projectStats?.completed_integrations, isCurrentPartner, activeContractKpi]);

  const contractStatus = useMemo(() => {
    const apiContractStatus = isCurrentPartner
      ? data?.contractStatus ?? null
      : null;

    if (apiContractStatus) {
      return mapContractStatusToCardData(apiContractStatus);
    }

    return PLACEHOLDER_CONTRACT_STATUS;
  }, [data?.contractStatus, isCurrentPartner]);

  const supportStatus = useMemo(() => {
    const summary = isCurrentPartner ? data?.summary ?? null : null;

    if (summary) {
      return mapSupportStatusFromSummary(summary);
    }

    return STATIC_PARTNER_DASHBOARD_DATA.supportStatus;
  }, [data?.summary, isCurrentPartner]);

  const partnerProgram = useMemo(() => {
    const summary = isCurrentPartner ? data?.summary ?? null : null;

    if (summary) {
      return mapSummaryToPartnerProgram(summary);
    }

    return PLACEHOLDER_PARTNER_PROGRAM;
  }, [data?.summary, isCurrentPartner]);

  const orders = useMemo(() => {
    const recentOrderStats = isCurrentPartner
      ? data?.recentOrderStats ?? null
      : null;

    if (recentOrderStats) {
      return mapRecentOrdersToCardData(recentOrderStats);
    }

    return [];
  }, [data?.recentOrderStats, isCurrentPartner]);

  const { projects, projectFilters } = useMemo(() => {
    const projectStats = isCurrentPartner ? data?.projectStats ?? null : null;

    if (projectStats) {
      return mapProjectStatsToDashboard(projectStats);
    }

    return EMPTY_PROJECT_STATUS;
  }, [data?.projectStats, isCurrentPartner]);

  return {
    ...STATIC_PARTNER_DASHBOARD_DATA,
    kpiCards,
    contractStatus,
    supportStatus,
    partnerProgram,
    orders,
    projects,
    projectFilters,
    isLoading,
    isSupportStatusSaving: status.supportStatusSaving,
    supportStatusError: status.supportStatusError,
    saveSupportStatus,
    clearSupportStatusError,
    sendContractReminder,
    isContractReminderSending: status.contractReminderLoading,
  };
}
